import { camelize } from "camelscore";
import {
  get as getFromCache,
  put as putToCache,
} from "storefront/lib/memoryCache";
import type { PublicConfig } from "../../../Config/PublicConfig";
import { get } from "../../request";
import type { Options } from "../../request/Options";

type Response = {
  data: PublicConfig;
};

export const CACHE_KEY = "public_config";

const unpack = ({ data }: Response): PublicConfig => data;

export const updateCache = (data: PublicConfig): void =>
  putToCache(CACHE_KEY, data);

// The config is the same for every user, so only the stageX namespace (which picks the Rails app)
// and the request id (which ties the Rails log line to the page render) are forwarded. Forwarding the
// cookie would make Rails reject config requests from banned or deleted users, failing the render.
//
// The cache is shared by every request this Next.js process serves, and stageX namespaces can route
// here alongside staging traffic. Keying by namespace keeps one namespace's config from being served
// to another.
const namespaceFrom = (options?: Options): string | undefined => {
  const namespace = options?.headers?.["x-goat-namespace"];
  return typeof namespace === "string" && namespace ? namespace : undefined;
};

const getConfig = (options?: Options): Promise<PublicConfig> => {
  if (typeof window !== "undefined" && window.PUBLIC_CONFIG) {
    const windowValue = camelize(window.PUBLIC_CONFIG);
    updateCache(windowValue);
    return Promise.resolve(windowValue);
  }
  const namespace = namespaceFrom(options);
  const cacheKey = namespace ? `${CACHE_KEY}:${namespace}` : CACHE_KEY;
  const cachedValue = getFromCache(cacheKey);
  if (cachedValue) {
    return Promise.resolve(cachedValue);
  }
  const requestOptions = options?.headers
    ? {
        headers: {
          "x-goat-namespace": options.headers["x-goat-namespace"],
          "x-request-id": options.headers["x-request-id"],
        },
      }
    : undefined;
  return get<Response>("/api/config", {}, requestOptions)
    .then(unpack)
    .then((data) => {
      putToCache(cacheKey, data);
      return data;
    });
};

export const getConfigSync = (): PublicConfig => {
  if (typeof window !== "undefined" && window.PUBLIC_CONFIG) {
    const windowValue = camelize(window.PUBLIC_CONFIG);
    updateCache(windowValue);
    return windowValue;
  }

  const cachedValue = getFromCache(CACHE_KEY);

  if (cachedValue) {
    return cachedValue;
  }

  throw new Error(
    "PublicConfig is not available synchronously. Ensure getConfig() has been called during SSR or that window.PUBLIC_CONFIG is set.",
  );
};

export default getConfig;
